import { useEffect, useMemo, useState } from "react";
import { Search, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar } from "@/components/chat/Avatar";

const lastSeen = (c) => (c.online ? "Online" : c.last_seen ? `Last seen ${new Date(c.last_seen).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : c.department || c.email);

// mode "direct": pick one colleague. mode "group": name + pick several (editing when `group` is given).
export function ContactPicker({ open, mode, online, onClose, onDone, group }) {
    const [contacts, setContacts] = useState([]);
    const [q, setQ] = useState("");
    const [picked, setPicked] = useState([]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setQ(""); setPicked(group ? group.members.map((m) => m.id) : []); setName(group?.name || "");
        api.get("/chat/contacts").then(({ data }) => setContacts(data || [])).catch(() => toast.error("Could not load colleagues"));
    }, [open, group?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = useMemo(() => {
        const t = q.trim().toLowerCase();
        return contacts
            .map((c) => ({ ...c, online: online.has(c.id) }))
            .filter((c) => !t || [c.name, c.email, c.department].some((v) => (v || "").toLowerCase().includes(t)))
            .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
    }, [contacts, q, online]);

    const choose = async (c) => {
        if (mode === "group") return setPicked((p) => (p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]));
        setBusy(true);
        try { const { data } = await api.post("/chat/conversations/direct", { user_id: c.id }); onDone(data); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not open chat"); }
        finally { setBusy(false); }
    };
    const createGroup = async () => {
        setBusy(true);
        try {
            if (group) {
                await api.put(`/chat/conversations/${group.id}/members`, { name, member_ids: picked });
                toast.success("Group updated");
                onDone(group);
            } else {
                const { data } = await api.post("/chat/conversations/group", { name, member_ids: picked });
                onDone(data);
            }
        }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save group"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md p-0 gap-0" data-testid={`chat-picker-${mode}`}>
                <DialogHeader className="p-5 pb-3"><DialogTitle>{mode === "group" ? (group ? "Edit group" : "New group") : "New chat"}</DialogTitle></DialogHeader>
                <div className="px-5 space-y-3">
                    {mode === "group" && (
                        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name, e.g. Operations team" maxLength={60}
                            className="w-full border border-ink/15 px-3 py-2 text-sm focus:outline-none focus:border-klein" data-testid="chat-group-name-input" />
                    )}
                    <label className="flex items-center gap-2 border border-ink/15 rounded-full px-3 py-2">
                        <Search className="w-4 h-4 text-ink/40" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or department" className="flex-1 text-sm outline-none bg-transparent" data-testid="chat-contact-search" />
                    </label>
                </div>
                <div className="mt-3 max-h-[50vh] overflow-y-auto border-t border-ink/10">
                    {rows.map((c) => (
                        <button key={c.id} disabled={busy} onClick={() => choose(c)} className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-ink/[0.03]" data-testid={`chat-contact-${c.id}`}>
                            <Avatar name={c.name} size="sm" online={c.online} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{c.name}</div>
                                <div className={`text-xs truncate ${c.online ? "text-emerald-600" : "text-ink/45"}`}>{lastSeen(c)}</div>
                            </div>
                            {mode === "group" && picked.includes(c.id) && <Check className="w-4 h-4 text-klein" />}
                        </button>
                    ))}
                    {rows.length === 0 && <div className="p-6 text-center text-sm text-ink/40">No colleagues found</div>}
                </div>
                {mode === "group" && (
                    <div className="p-4 border-t border-ink/10 flex items-center justify-between">
                        <span className="text-xs text-ink/50">{picked.length} selected</span>
                        <button onClick={createGroup} disabled={busy || !name.trim() || !picked.length} className="bg-klein text-white px-4 py-2 text-sm disabled:opacity-40" data-testid="chat-group-create-btn">{group ? "Save changes" : "Create group"}</button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ContactPicker;
